import { useState, useEffect } from 'react';
import PageHeader from '../components/ui/page-header';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileText, 
  Download,
  Eye,
  Loader2,
  Calendar,
  Euro,
  CheckCircle,
  Clock,
  AlertCircle,
  CreditCard,
  TrendingUp,
  Receipt,
  History,
  Filter,
  ArrowUpDown,
  Search,
  ClipboardList,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import AuditLogPanel from '../components/AuditLogPanel';

const InvoicesPage = () => {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [hoveredInvoice, setHoveredInvoice] = useState(null);
  const [payConfirmOpen, setPayConfirmOpen] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/user/invoices');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (invoiceId) => {
    toast.info(`Download factuur ${invoiceId} - Binnenkort beschikbaar`);
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailOpen(true);
  };

  const handlePay = (invoice) => {
    setPayingInvoice(invoice);
    setPayConfirmOpen(true);
  };

  const confirmPayment = () => {
    toast.info(`Betaling voor factuur ${payingInvoice.invoice_number || payingInvoice.id} wordt verwerkt...`);
    setPayConfirmOpen(false);
    setPayingInvoice(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Betaald
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            In afwachting
          </Badge>
        );
      case 'overdue':
        return (
          <Badge className="bg-red-50 text-red-700 border-red-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Verlopen
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const totalPaid = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.amount, 0);

  const totalOpen = invoices
    .filter(inv => inv.status === 'pending' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + inv.amount, 0);

  const overdueCount = invoices.filter(inv => inv.status === 'overdue').length;

  // Filter & sort
  const filteredInvoices = invoices
    .filter(inv => statusFilter === 'all' || inv.status === statusFilter)
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') cmp = new Date(a.date) - new Date(b.date);
      else if (sortField === 'amount') cmp = a.amount - b.amount;
      else if (sortField === 'status') cmp = a.status.localeCompare(b.status);
      return sortDir === 'desc' ? -cmp : cmp;
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 lg:p-8 space-y-8">
        {/* Header */}
        <PageHeader
          title="Facturen"
          tooltip="Hier vindt u al uw facturen. U kunt facturen downloaden als PDF en openstaande facturen betalen."
          testId="invoices-title"
        />

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Receipt className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{invoices.length}</p>
                  <p className="text-xs text-neutral-500">Totaal facturen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">&euro;{totalPaid.toFixed(2)}</p>
                  <p className="text-xs text-neutral-500">Totaal betaald</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center">
                  <Clock className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">&euro;{totalOpen.toFixed(2)}</p>
                  <p className="text-xs text-neutral-500">Openstaand</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] ${overdueCount > 0 ? 'ring-1 ring-red-200' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-neutral-100 text-neutral-600 flex items-center justify-center">
                  <Calendar className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">
                    {invoices.length > 0 ? new Date(invoices[0].date).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' }) : '-'}
                  </p>
                  <p className="text-xs text-neutral-500">Laatste factuur</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overdue Alert */}
        {overdueCount > 0 && (
          <Card className="border-red-200 bg-red-50 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">
                  {overdueCount} factuur{overdueCount !== 1 ? 'en' : ''} verlopen
                </p>
                <p className="text-xs text-red-700">
                  Betaal uw openstaande facturen om onderbreking van diensten te voorkomen
                </p>
              </div>
              <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={() => {
                const overdue = invoices.find(inv => inv.status === 'overdue');
                if (overdue) handlePay(overdue);
              }}>
                <CreditCard className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nu betalen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Payment summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-neutral-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-3">Betalingsstatus</p>
              <div className="space-y-2">
                {[
                  { label: 'Betaald', count: invoices.filter(i => i.status === 'paid').length, color: 'bg-green-500' },
                  { label: 'Openstaand', count: invoices.filter(i => i.status === 'pending').length, color: 'bg-yellow-500' },
                  { label: 'Verlopen', count: overdueCount, color: 'bg-red-500' },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-3">
                    <div className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                    <span className="text-sm text-neutral-600 flex-1">{s.label}</span>
                    <span className="text-sm font-semibold text-black">{s.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-3">Financieel overzicht</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-neutral-600">Totaal gefactureerd</span><span className="font-semibold">&euro;{(totalPaid + totalOpen).toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-neutral-600">Betaald</span><span className="font-semibold text-green-600">&euro;{totalPaid.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-neutral-600">Openstaand</span><span className="font-semibold text-yellow-600">&euro;{totalOpen.toFixed(2)}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-lg font-semibold">Factuuroverzicht</CardTitle>
                <CardDescription>
                  {filteredInvoices.length} van {invoices.length} facturen
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px] h-8 text-xs border-neutral-200" data-testid="invoice-status-filter">
                    <Filter className="h-3 w-3 mr-1.5 text-neutral-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle statussen</SelectItem>
                    <SelectItem value="paid">Betaald</SelectItem>
                    <SelectItem value="pending">In afwachting</SelectItem>
                    <SelectItem value="overdue">Verlopen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredInvoices.length > 0 ? (
              <div className="overflow-x-auto relative">
                <table className="w-full" data-testid="invoices-table">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 p-4">Factuurnummer</th>
                      <th className="text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 p-4 cursor-pointer select-none hover:text-black transition-colors" onClick={() => toggleSort('date')}>
                        <span className="inline-flex items-center gap-1">
                          Datum
                          <ArrowUpDown className={`h-3 w-3 ${sortField === 'date' ? 'text-black' : 'text-neutral-300'}`} />
                        </span>
                      </th>
                      <th className="text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500 p-4">Omschrijving</th>
                      <th className="text-right text-[10px] font-bold uppercase tracking-wider text-neutral-500 p-4 cursor-pointer select-none hover:text-black transition-colors" onClick={() => toggleSort('amount')}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          Bedrag
                          <ArrowUpDown className={`h-3 w-3 ${sortField === 'amount' ? 'text-black' : 'text-neutral-300'}`} />
                        </span>
                      </th>
                      <th className="text-center text-[10px] font-bold uppercase tracking-wider text-neutral-500 p-4 cursor-pointer select-none hover:text-black transition-colors" onClick={() => toggleSort('status')}>
                        <span className="inline-flex items-center gap-1">
                          Status
                          <ArrowUpDown className={`h-3 w-3 ${sortField === 'status' ? 'text-black' : 'text-neutral-300'}`} />
                        </span>
                      </th>
                      <th className="text-right text-[10px] font-bold uppercase tracking-wider text-neutral-500 p-4">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredInvoices.map((invoice) => (
                      <tr 
                        key={invoice.id} 
                        className="hover:bg-neutral-50 relative group" 
                        data-testid={`invoice-row-${invoice.id}`}
                        onMouseEnter={() => setHoveredInvoice(invoice.id)}
                        onMouseLeave={() => setHoveredInvoice(null)}
                      >
                        <td className="p-4">
                          <span className="text-sm font-medium text-black">{invoice.invoice_number || invoice.id}</span>
                        </td>
                        <td className="p-4 text-sm text-neutral-600">
                          {new Date(invoice.date).toLocaleDateString('nl-NL')}
                        </td>
                        <td className="p-4 text-sm text-neutral-600">
                          {invoice.description}
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-sm font-medium text-black">&euro;{invoice.amount.toFixed(2)}</span>
                        </td>
                        <td className="p-4 text-center">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 gap-1"
                              onClick={() => handleView(invoice)}
                              title="Bekijken"
                              aria-label="Bekijk factuur"
                              data-testid={`view-invoice-${invoice.id}`}
                            >
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                              <span className="text-xs">Bekijken</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 gap-1"
                              onClick={() => handleDownload(invoice.id)}
                              title="Downloaden"
                              aria-label="Download factuur"
                              data-testid={`download-invoice-${invoice.id}`}
                            >
                              <Download className="h-4 w-4" strokeWidth={1.5} />
                              <span className="text-xs">Download</span>
                            </Button>
                            {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                              <Button
                                size="sm"
                                className="h-8 bg-black text-white hover:bg-neutral-800 text-xs px-3"
                                onClick={() => handlePay(invoice)}
                                data-testid={`pay-invoice-${invoice.id}`}
                              >
                                <CreditCard className="h-3 w-3 mr-1" strokeWidth={1.5} />
                                Betaal
                              </Button>
                            )}
                          </div>
                        </td>

                        {/* Hover Preview - positioned to the left to avoid covering actions */}
                        {hoveredInvoice === invoice.id && (
                          <td className="absolute left-4 -top-1 z-10 pointer-events-none" colSpan={1}>
                            <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 w-52 text-xs space-y-1.5" data-testid={`invoice-preview-${invoice.id}`}>
                              <p className="font-medium text-black">{invoice.invoice_number || invoice.id}</p>
                              <div className="flex justify-between">
                                <span className="text-neutral-500">Datum</span>
                                <span>{new Date(invoice.date).toLocaleDateString('nl-NL')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-neutral-500">Bedrag</span>
                                <span className="font-medium">&euro;{invoice.amount.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-neutral-500">BTW (21%)</span>
                                <span>&euro;{(invoice.amount * 0.21).toFixed(2)}</span>
                              </div>
                              {invoice.due_date && (
                                <div className="flex justify-between">
                                  <span className="text-neutral-500">Vervaldatum</span>
                                  <span>{new Date(invoice.due_date).toLocaleDateString('nl-NL')}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-1">
                  {statusFilter !== 'all' ? 'Geen facturen met deze status' : 'Geen facturen'}
                </h3>
                <p className="text-sm text-neutral-500">
                  {statusFilter !== 'all' ? 'Probeer een ander filter' : 'Er zijn nog geen facturen voor uw account'}
                </p>
                {statusFilter !== 'all' && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => setStatusFilter('all')}>
                    Filter wissen
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg">
            {selectedInvoice && (
              <>
                <DialogHeader>
                  <DialogTitle>Factuur {selectedInvoice.invoice_number || selectedInvoice.id}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500">Datum</p>
                      <p className="text-sm font-medium text-black">{new Date(selectedInvoice.date).toLocaleDateString('nl-NL')}</p>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500">Status</p>
                      <div className="mt-1">{getStatusBadge(selectedInvoice.status)}</div>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500">Bedrag</p>
                      <p className="text-lg font-bold text-black">&euro;{selectedInvoice.amount.toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500">BTW (21%)</p>
                      <p className="text-sm font-medium text-neutral-700">&euro;{(selectedInvoice.amount * 0.21).toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-xs text-neutral-500">Omschrijving</p>
                    <p className="text-sm text-black mt-1">{selectedInvoice.description}</p>
                  </div>
                  {selectedInvoice.due_date && (
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <p className="text-xs text-neutral-500">Vervaldatum</p>
                      <p className="text-sm font-medium text-black">{new Date(selectedInvoice.due_date).toLocaleDateString('nl-NL')}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => handleDownload(selectedInvoice.id)}>
                      <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Download PDF
                    </Button>
                    {(selectedInvoice.status === 'pending' || selectedInvoice.status === 'overdue') && (
                      <Button className="flex-1 bg-black text-white hover:bg-neutral-800" onClick={() => { setDetailOpen(false); handlePay(selectedInvoice); }}>
                        <CreditCard className="h-4 w-4 mr-2" strokeWidth={1.5} />
                        Betaal nu
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Confirmation Dialog */}
        <Dialog open={payConfirmOpen} onOpenChange={setPayConfirmOpen}>
          <DialogContent className="sm:max-w-md" data-testid="pay-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Betaling bevestigen</DialogTitle>
              <DialogDescription>
                Controleer de gegevens voordat u de betaling verwerkt.
              </DialogDescription>
            </DialogHeader>
            {payingInvoice && (
              <div className="space-y-3 py-2">
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-500">Factuurnummer</span>
                    <span className="text-sm font-medium text-black">{payingInvoice.invoice_number || payingInvoice.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-500">Omschrijving</span>
                    <span className="text-sm text-black">{payingInvoice.description}</span>
                  </div>
                  <div className="h-px bg-neutral-200" />
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-500">Bedrag excl. BTW</span>
                    <span className="text-sm text-black">&euro;{payingInvoice.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-500">BTW (21%)</span>
                    <span className="text-sm text-black">&euro;{(payingInvoice.amount * 0.21).toFixed(2)}</span>
                  </div>
                  <div className="h-px bg-neutral-200" />
                  <div className="flex justify-between">
                    <span className="text-sm font-semibold text-black">Totaal</span>
                    <span className="text-lg font-bold text-black">&euro;{(payingInvoice.amount * 1.21).toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <CreditCard className="h-4 w-4 text-blue-600 shrink-0" strokeWidth={1.5} />
                  <span className="text-xs text-blue-700">Betaling via iDEAL / Bankoverschrijving</span>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPayConfirmOpen(false)} data-testid="pay-cancel-button">
                Annuleren
              </Button>
              <Button 
                className="bg-black text-white hover:bg-neutral-800" 
                onClick={confirmPayment}
                data-testid="pay-confirm-button"
              >
                <CreditCard className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Bevestig Betaling
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default InvoicesPage;
